import { defineStore, acceptHMRUpdate } from 'pinia';
import PlanningElement from '@/models/PlanningElement';
import Action from '@/models/Action';
import i18n from '@/js/plugins/i18n';
import useUsersStore from '@/stores/users';
import useUsersGroupsStore from '@/stores/users/groups';
import useMultiprojectsPlanningsStore from '@/stores/multiprojects/plannings';
import useUserOptionsStore from '@/stores/user-options';

export function getProjectIdsByCategory(projects) {
  return projects.reduce((acc, project) => {
    if (project.meta?.category) {
      project.meta.category.split(',').forEach((tag) => {
        if (! acc[tag]) acc[tag] = [];
        acc[tag].push(project.id);
      });
    }
    return acc;
  }, {});
}

const filterProps = ['selectedProjects', 'selectedVirtualParticipants', 'selectedCompanyUsers', 'selectedDeletedUsers', 'selectedActionDeletedUsers', 'selectedCompanyGroups',
  'selectedActionVirtualParticipants', 'selectedDates', 'selectedDisplay', 'calendarDisplay',
  'filterOptions', 'progressOptions', 'actionsOptions', 'portfolioOptions', 'processOptions', 'groupsOptions'];

let loadFiltersPromise = Promise.resolve();

const store = defineStore('multiprojects', {
  state: () => ({
    loading: { complete: false, counter: { toLoad: 0, loaded: 0 } },
    projects: [],
    selectedProjects: null,
    virtualParticipants: [],
    selectedVirtualParticipants: [],
    actionVirtualParticipants: [],
    selectedActionVirtualParticipants: null,
    companyUsers: [],
    selectedCompanyUsers: null,
    deletedUsers: [],
    selectedDeletedUsers: [],
    actionDeletedUsers: [],
    selectedActionDeletedUsers: [],
    companyGroups: [],
    selectedCompanyGroups: null,
    meetingActions: [],
    selectedDates: { starttime: null, endtime: null },
    selectedDisplay: {
      description: false, checklist: false, progress: false, users: false, dates: false, links: false, budgets: false, projecttitle: false,
    },
    calendarDisplay: {
      milestone: true, shorttask: true, longtask: true, planningactions: true, meetingactions: true, holidays: true,
    },
    filterOptions: { el_type: 'both', hide_elements: false, unassigned: false },
    progressOptions: { selectedCols: {} },
    actionsOptions: { groupBy: 'user', displayCompleted: false, withWorkloadOnly: false, title: '' },
    portfolioOptions: { displayArchived: false, selectedProjectOwners: [], portfolioView: 'table-view', selectedCols: {}, groupByCategory: true },
    processOptions: { selectedSteps: {} },
    groupsOptions: { useGroups: false, hideUsers: false },
    clientCustomOptions: {},
    selectedCompleteCategories: null, // store temporarily when loading filter
  }),

  getters: {
    getShowDetailsItems() {
      const config = {
        description: this.selectedDisplay.description,
        progress: this.selectedDisplay.progress,
        step: true,
        checklist: this.selectedDisplay.checklist,
        users: this.selectedDisplay.users,
        date: this.selectedDisplay.dates,
        dates: this.selectedDisplay.dates,
        links: this.selectedDisplay.links,
        budgets: this.selectedDisplay.budgets,
        projecttitle: this.selectedDisplay.projecttitle,
      };
      return Object.keys(config).filter(key => config[key]);
    },

    planningActions() {
      const actions = [];
      this.projects.forEach((project) => {
        if (! project.elements) return;
        project.elements.forEach((el) => {
          const elementActions = (el.getChecklist() || []).map(item => new Action({ type: 'planning', element: el, item }));
          Array.prototype.push.apply(actions, elementActions);
        });
      });
      return actions;
    },

    getOriginalPlanningEl() {
      return (el) => {
        if (el.dashboard_el) return el; // already an originalPlanningEl (case switching tabs inside element details)

        const originalPlanning = this.getPlanningById(el.project_id);
        const originalPlanningEl = new PlanningElement(originalPlanning, { ...el.getAll(), id: parseInt(el.id, 10) });
        originalPlanningEl.dashboard_el = el;
        return originalPlanningEl;
      };
    },

    getPlanningById() {
      const planningsStore = useMultiprojectsPlanningsStore();
      return id => planningsStore.byId[id];
    },

    getAllPlannings() {
      const planningsStore = useMultiprojectsPlanningsStore();
      return Object.keys(planningsStore.byId).map(id => planningsStore.byId[id]);
    },
  },

  actions: {
    async load() {
      const usersStore = useUsersStore();
      const groupsStore = useUsersGroupsStore();
      const planningsStore = useMultiprojectsPlanningsStore();

      planningsStore.loadingCounter = { ...planningsStore.loadingCounter, toLoad: 0, loaded: 0 };
      this.loading.counter = planningsStore.loadingCounter;
      this.loading.complete = false;

      await usersStore.userPromise; // needed for projects status + refresh ui
      const loadAllPlanningsPromise = planningsStore.loadAllPlannings();

      await loadFiltersPromise;
      await usersStore.getOrganizationPromise;
      const companyUsers = angular.copy(usersStore.getOrganizationUsers);
      companyUsers.forEach((user) => {
        user.username = usersStore.getUsername(user);
      });

      await usersStore.getGroupsPromise;
      const { companyGroups } = groupsStore;

      const projects = await loadAllPlanningsPromise;
      await new Promise((resolve) => { setTimeout(resolve, 300); }); // refresh ui

      const virtualParticipants = this.virtualParticipants.slice();
      const virtualParticipantsUsernames = new Set(virtualParticipants.map(item => item.username));
      const deletedUsers = this.deletedUsers.slice();
      const deletedUserIds = new Set(deletedUsers.map(item => item.id));
      const actionVirtualParticipants = this.actionVirtualParticipants.slice();
      const actionVirtualParticipantsUsernames = new Set(actionVirtualParticipants.map(item => item.username));
      const actionDeletedUsers = this.actionDeletedUsers.slice();
      const actionDeletedUserIds = new Set(actionDeletedUsers.map(item => item.id));

      projects.forEach((planning) => {
        if (! planning || ! planning.elements) return;
        planning.elements.forEach((el) => {
          if (! el) return;

          (el.getUsers() || []).forEach((user) => {
            if (user.group_id) return;
            const userId = user.id;
            if (! userId) { // virtual participant
              const username = usersStore.getUsername(user);
              if (! username || virtualParticipantsUsernames.has(username)) return;
              virtualParticipantsUsernames.add(username);
              virtualParticipants.push({ username });
              return;
            }

            if (! usersStore.getUserById(userId) && ! deletedUserIds.has(userId)) { // deleted user
              const username = usersStore.getUsername(user, 'short');
              deletedUserIds.add(userId);
              deletedUsers.push({
                id: userId,
                username: `${i18n.global.t('MULTIPROJECTS.DELETED_USER')} ${username ? `(${username})` : deletedUsers.length + 1}`,
                isDeleted: true,
              });
            }
          });

          (el.getChecklist() || []).forEach((checklistItem) => {
            if (checklistItem.group_id) return;
            const { user_id: userId, username } = checklistItem;
            if (! userId) { // virtual participant
              if (! username || actionVirtualParticipantsUsernames.has(username)) return;
              actionVirtualParticipantsUsernames.add(username);
              actionVirtualParticipants.push({ username });
              return;
            }

            if (! usersStore.getUserById(userId) && ! actionDeletedUserIds.has(userId)) { // deleted user
              actionDeletedUserIds.add(userId);
              actionDeletedUsers.push({
                id: userId,
                username: `${i18n.global.t('MULTIPROJECTS.DELETED_USER')} ${username ? `(${username})` : actionDeletedUsers.length + 1}`,
                isDeleted: true,
              });
            }
          });
        });
      });

      virtualParticipants.sort((a, b) => (a.username.toLowerCase() < b.username.toLowerCase() ? -1 : 1));
      actionVirtualParticipants.sort((a, b) => (a.username.toLowerCase() < b.username.toLowerCase() ? -1 : 1));
      projects.sort((a, b) => {
        if (a?.meta && b?.meta) return (a.meta.title || "").toLowerCase() < (b.meta.title || "").toLowerCase() ? -1 : 1;
        return a?.meta ? -1 : 1;
      });

      const filtersSelections = this.cleanSelectedFilters({ companyUsers, companyGroups, projects, virtualParticipants, deletedUsers, actionVirtualParticipants, actionDeletedUsers });

      this.$patch((state) => {
        state.companyUsers = companyUsers;
        state.companyGroups = companyGroups;
        state.projects = projects; // ref binding to store plannings // warning : not reactive if plannings refs change
        state.virtualParticipants = virtualParticipants;
        state.deletedUsers = deletedUsers;
        state.actionVirtualParticipants = actionVirtualParticipants;
        state.actionDeletedUsers = actionDeletedUsers;
        state.selectedCompanyUsers = filtersSelections.selectedCompanyUsers;
        state.selectedCompanyGroups = filtersSelections.selectedCompanyGroups;
        state.selectedProjects = filtersSelections.selectedProjects;
        state.selectedVirtualParticipants = filtersSelections.selectedVirtualParticipants;
        state.selectedDeletedUsers = filtersSelections.selectedDeletedUsers;
        state.selectedActionVirtualParticipants = filtersSelections.selectedActionVirtualParticipants;
        state.selectedActionDeletedUsers = filtersSelections.selectedActionDeletedUsers;
        state.loading.complete = true;
      });

      return projects;
    },

    reload() {
      return this.load();
    },

    async loadMeetingActions() {
      const usersStore = useUsersStore();
      await usersStore.getOrganizationPromise;
      if (this.selectedCompanyUsers) {
        this.selectedCompanyUsers = _.intersection(this.selectedCompanyUsers, usersStore.getOrganizationUsers.map(item => item.id)); // handle deleted
      } else {
        this.selectedCompanyUsers = usersStore.getOrganizationUsers.map(item => item.id);
      }
      return Promise.all([window.apiSrv.call('v2/meetings/actions', 'index'), window.apiSrv.call('v2/meetings/actions/completed', 'index')]).then((results) => {
        const meetingActions = results && results[0] && results[0].data && results[0].data.actions || [];
        Array.prototype.push.apply(meetingActions, results && results[1] && results[1].data && results[1].data.actions || []);
        this.meetingActions = meetingActions.map(item => new Action({ type: 'meeting', item }));
        this.meetingActions.forEach((action) => {
          const userId = action.getUserId();
          if (! userId) return;
          if (! usersStore.getUserById(userId) && ! this.actionDeletedUsers.find(item => item.id == userId)) { // deleted user
            const username = action.getUsername();
            this.actionDeletedUsers.push({
              id: userId,
              username: `${i18n.global.t('MULTIPROJECTS.DELETED_USER')} ${username ? `(${username})` : this.actionDeletedUsers.length + 1}`,
              isDeleted: true,
            });
          }
        });
        return this.meetingActions;
      });
    },

    loadFilters({ dashboardName, ...defaultValues } = {}) {
      // load all dashboardName filters and apply the first one
      if (! dashboardName) return;
      loadFiltersPromise = (async () => {
        const userOptionsStore = useUserOptionsStore();
        const userOptionsConfigs = await userOptionsStore.loadOption(`dashfilters-${dashboardName}`); // [config1, config2, ...]

        let config = userOptionsConfigs?.[0];
        if (! userOptionsConfigs) {
          // temp: move legacy config to user options
          const legacyConfig = window.safeParseJSON(window.localStorageWrapper.getItem(`${dashboardName}.filters`));
          if (_.isObject(legacyConfig)) {
            config = { name: i18n.global.t('MULTIPROJECTS.SAVE_FILTERS.DEFAULT'), ...legacyConfig };
            await userOptionsStore.saveUserOption({ key: `dashfilters-${dashboardName}`, value: [config] });
            userOptionsStore.userOptions[`dashfilters-${dashboardName}`] = [config];
            window.localStorageWrapper.removeItem(`${dashboardName}.filters`);
          }
        }

        this.applyFiltersConfig(config || {}, defaultValues);
      })();
    },
    cleanSelectedFilters({
      companyUsers = this.companyUsers, companyGroups = this.companyGroups, projects = this.projects,
      virtualParticipants = this.virtualParticipants, deletedUsers = this.deletedUsers,
      actionVirtualParticipants = this.actionVirtualParticipants, actionDeletedUsers = this.actionDeletedUsers,
      } = {}) {
      // return selected filters with non existing values removed
      let selectedCompanyUsers;
      if (this.selectedCompanyUsers) {
        selectedCompanyUsers = _.intersection(this.selectedCompanyUsers, companyUsers.map(item => item.id)); // handle deleted
      } else {
        selectedCompanyUsers = companyUsers.map(item => item.id);
      }

      let selectedCompanyGroups;
      if (this.selectedCompanyGroups) {
        selectedCompanyGroups = _.intersection(this.selectedCompanyGroups, companyGroups.map(item => item.id)); // handle deleted
      } else {
        selectedCompanyGroups = companyGroups.map(item => item.id);
      }

      let selectedProjects;
      if (this.selectedProjects) {
        selectedProjects = _.intersection(this.selectedProjects, projects.map(item => item.id).filter(item => item)); // handle deleted or loading failure
      } else {
        selectedProjects = projects.map(item => item.id).filter(item => item);
      }
      if (this.selectedCompleteCategories?.length) {
        const projectIdsByCategory = getProjectIdsByCategory(projects);
        const projectIdsByCompleteCategory = this.selectedCompleteCategories.map(category => projectIdsByCategory[category] || []);
        const completeCategoryProjects = [].concat(...projectIdsByCompleteCategory);
        selectedProjects = [...new Set(selectedProjects.concat(completeCategoryProjects))];
        this.selectedCompleteCategories = null;
      }

      let selectedVirtualParticipants = virtualParticipants.map(item => item.username);
      if (this.selectedVirtualParticipants) {
        selectedVirtualParticipants = _.intersection(this.selectedVirtualParticipants, selectedVirtualParticipants); // handle deleted
      }

      let selectedActionVirtualParticipants = actionVirtualParticipants.map(item => item.username);
      if (this.selectedActionVirtualParticipants) {
        selectedActionVirtualParticipants = _.intersection(this.selectedActionVirtualParticipants, selectedActionVirtualParticipants); // handle deleted
      }

      let selectedDeletedUsers = deletedUsers.map(item => item.id);
      if (this.selectedDeletedUsers) {
        selectedDeletedUsers = _.intersection(this.selectedDeletedUsers, selectedDeletedUsers); // handle deleted
      }

      let selectedActionDeletedUsers = actionDeletedUsers.map(item => item.id);
      if (this.selectedActionDeletedUsers) {
        selectedActionDeletedUsers = _.intersection(this.selectedActionDeletedUsers, selectedActionDeletedUsers); // handle deleted
      }

      return {
        selectedCompanyUsers,
        selectedCompanyGroups,
        selectedProjects,
        selectedVirtualParticipants,
        selectedDeletedUsers,
        selectedActionVirtualParticipants,
        selectedActionDeletedUsers,
      };
    },
    applyFiltersConfig(config = {}, defaultValues = {}) {
      this.selectedCompleteCategories = config.selectedCompleteCategories; // used in cleanSelectedFilters
      filterProps.forEach((key) => {
        const filterValue = angular.copy(typeof config[key] != 'undefined' ? config[key] : defaultValues[key]);
        if (typeof filterValue == 'object') {
          if (_.isArray(filterValue)) {
            this[key] = filterValue;
          } else if (filterValue === null) {
            this[key] = null;
          } else {
            _.extend(this[key], filterValue);
          }
        }
      });
    },
    getCurrentFiltersConfig() {
      return filterProps.reduce((acc, item) => {
        acc[item] = angular.copy(this[item]);
        if (item == 'selectedProjects') { // case all projects
          const active = this.projects.filter(project => project.id).map(project => project.id);
          if (active.length == this.selectedProjects.length && active.length == _.intersection(active, this.selectedProjects).length) acc[item] = null;
          if (acc[item]?.length) { // look for complete categories
            const projectIdsByCategory = getProjectIdsByCategory(this.projects);
            const selectedCompleteCategories = Object.keys(projectIdsByCategory).filter((category) => {
              const selectedCategoryProjects = _.intersection(this.selectedProjects, projectIdsByCategory[category]);
              return selectedCategoryProjects.length == projectIdsByCategory[category].length;
            });
            if (selectedCompleteCategories.length) acc.selectedCompleteCategories = selectedCompleteCategories;
          }
        }
        if (item == 'selectedCompanyUsers') { // case all company users
          const active = this.companyUsers.map(user => user.id);
          if (active.length == this.selectedCompanyUsers.length && active.length == _.intersection(active, this.selectedCompanyUsers).length) acc[item] = null;
        }
        if (item == 'selectedCompanyGroups') { // case all company groups
          const active = this.companyGroups.map(group => group.id);
          if (active.length == this.selectedCompanyGroups.length && active.length == _.intersection(active, this.selectedCompanyGroups).length) acc[item] = null;
        }
        if (item == 'selectedVirtualParticipants') { // case all virtual participants
          const active = this.virtualParticipants.map(user => user.username);
          if (active.length == this.selectedVirtualParticipants.length && active.length == _.intersection(active, this.selectedVirtualParticipants).length) acc[item] = null;
        }
        if (item == 'selectedActionVirtualParticipants') { // case all action virtual participants
          const active = this.actionVirtualParticipants.map(user => user.username);
          if (active.length == this.selectedActionVirtualParticipants.length && active.length == _.intersection(active, this.selectedActionVirtualParticipants).length) acc[item] = null;
        }
        if (item == 'selectedDeletedUsers') { // case all deleted users
          const active = this.deletedUsers.map(group => group.id);
          if (active.length == this.selectedDeletedUsers.length && active.length == _.intersection(active, this.selectedDeletedUsers).length) acc[item] = null;
        }
        if (item == 'selectedActionDeletedUsers') { // case all action deleted users
          const active = this.actionDeletedUsers.map(group => group.id);
          if (active.length == this.selectedActionDeletedUsers.length && active.length == _.intersection(active, this.selectedActionDeletedUsers).length) acc[item] = null;
        }
        return acc;
      }, {});
    },
    saveFilters(dashboardName) {
      if (! dashboardName) return Promise.reject();
      const userOptionsStore = useUserOptionsStore();
      const value = userOptionsStore.getOption(`dashfilters-${dashboardName}`) || null;
      return userOptionsStore.saveUserOption({ key: `dashfilters-${dashboardName}`, value });
    },

    updateOriginalEl({ el, data }) {
      const invariantFields = ['id', 'lane_id', 'timeBar', 'dependencies', 'config']; // fields modified by multiprojects should not be updated
      el.set(_.omit(data, invariantFields));
    },
  },
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
export default store;
